import { orderBy } from "lodash";

import { GoalBasedGoalTypeMap } from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import {
  GoalBasedGoalRepeatsMap,
  GoalBasedRepeatTimeFrame,
  GoalBasedRepeatTimeFrameLabel,
  GoalBasedRepeatTimeFrameSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/types";

import getEntries from "@src/utils/entries";

import type { PlanDateTypeOptionsDropdown } from "./Types";

const goalOptionEntries = getEntries(GoalBasedGoalTypeMap);

export const goalOptions = orderBy(
  goalOptionEntries.map(([key, value]) => ({
    label: value,
    id: key,
  })),
  [(g) => g.label],
  ["asc"]
);

const repeatOptionEntries = getEntries(GoalBasedGoalRepeatsMap);

export const repeatTimeFrameOptions = repeatOptionEntries.map(
  ([key, value]) => ({
    label: value,
    value: key,
  })
);

export const AllRepeatOptions = repeatTimeFrameOptions;

export const NoneRepeatOption = repeatTimeFrameOptions.filter(
  (el) => el.value === GoalBasedRepeatTimeFrameSchema.Enum.None
);

export const RepeatOptionsNotNone = repeatTimeFrameOptions.filter(
  (el) => el.value !== GoalBasedRepeatTimeFrameSchema.Enum.None
);

export const FinancingMethods = ["Cash", "Lease", "Loan"] as const;

export const FinancingMethodOptions = FinancingMethods.map((fm) => ({
  label: fm,
  value: fm,
}));

export const planDateTypeOptionsDropdown: PlanDateTypeOptionsDropdown[] = [
  {
    label: "Already Started",
    value: "AlreadyStarted",
    dateType: "AlreadyStarted",
  },
  { label: "Calendar Year", value: "CalendarYear", dateType: "Year" },
  { label: "Your Retirement", value: "YourRetirement", dateType: "Age" },
  {
    label: "End of Your Planning Horizon",
    value: "EndOfYourPlanningHorizon",
    dateType: "Age",
  },
  { label: "Your Age", value: "YourAge", dateType: "Age" },
  { label: "Spouse's Retirement", value: "SpouseRetirement", dateType: "Age" },
  {
    label: "End of Spouse's Planning Horizon",
    value: "EndOfSpousePlanningHorizon",
    dateType: "Age",
  },
  { label: "Spouse's Age", value: "SpouseAge", dateType: "Age" },
];

// Singular Options
export const defaultRepeatsFieldOption = {
  label: "None" as GoalBasedRepeatTimeFrameLabel,
  value: "None" as GoalBasedRepeatTimeFrame,
};
export const AlreadyStartedOption = planDateTypeOptionsDropdown[0];
export const CalendarYearOption = planDateTypeOptionsDropdown[1];
export const YourRetirementOption = planDateTypeOptionsDropdown[2];
export const EndOfYourPlanningHorizonOption = planDateTypeOptionsDropdown[3];
export const YourAgeOption = planDateTypeOptionsDropdown[4];
export const SpousesRetirementOption = planDateTypeOptionsDropdown[5];
export const EndOfSpousesPlanningHorizonOption = planDateTypeOptionsDropdown[6];
export const SpousesAgeOption = planDateTypeOptionsDropdown[7];

// Array of Options
export const CalendarYearOptions = planDateTypeOptionsDropdown.filter(
  (el) => el.value === "CalendarYear"
);
export const YourAgeOptions = planDateTypeOptionsDropdown.filter(
  (el) => el.value === "YourAge"
);
export const SpousesAgeOptions = planDateTypeOptionsDropdown.filter(
  (el) => el.value === "SpouseAge"
);
export const SpouseOptions = planDateTypeOptionsDropdown.filter(
  (el) =>
    el.value === "SpouseRetirement" ||
    el.value === "EndOfSpousePlanningHorizon" ||
    el.value === "SpouseAge"
);

// Common Goal Based Form Fields
export const OwnerField = "OwnerField";
export const TargetAmountField = "TargetAmountField";
export const TargetRetirementAgeField = "TargetRetirementAgeField";
export const PlanningHorizonField = "PlanningHorizonField";
export const DateOfBirthField = "DateOfBirthField";
export const AnnualAmountField = "AnnualAmountField";
export const AmountField = "AmountField";
export const NameField = "NameField";
export const PurchasePriceField = "PurchasePriceField";
export const FinancingMethodField = "FinancingMethodField";
export const DownPaymentField = "DownPaymentField";
export const FinancingTermField = "FinancingTermField";
export const InterestRateField = "InterestRateField";
export const ResidualValueField = "ResidualValueField";
export const AnnualTuitionField = "AnnualTuitionField";
export const RepeatsField = "RepeatsField";
export const TargetDateField = "TargetDateField";
export const StartsField = "StartsField";
export const EndsField = "EndsField";
export const CalendarYearStartField = "CalendarYearStartField";
export const YourAgeStartField = "YourAgeStartField";
export const SpousesAgeStartField = "SpousesAgeStartField";
export const CalendarYearEndField = "CalendarYearEndField";
export const YourAgeEndField = "YourAgeEndField";
export const SpousesAgeEndField = "SpousesAgeEndField";
export const YearOfSaleField = "YearOfSaleField";
export const CostOfSaleField = "CostOfSaleField";
export const FromAccountField = "FromAccountField";
export const ToAccountField = "ToAccountField";
export const FilingAgeField = "FilingAgeField";
export const ExpensePeriodField = "ExpensePeriodField";
export const ApproachField = "ApproachField";
export const AutoTransportField = "AutoTransportField";
export const BillsUtilitiesField = "BillsUtilitiesField";
export const BusinessField = "BusinessField";
export const CharitableGivingField = "CharitableGivingField";
export const DiningOutField = "DiningOutField";
export const EducationField = "EducationField";
export const EntertainmentField = "EntertainmentField";
export const FeesChargesField = "FeesChargesField";
export const GiftField = "GiftField";
export const GroceriesField = "GroceriesField";
export const HealthFitnessField = "HealthFitnessField";
export const KidsField = "KidsField";
export const MiscField = "MiscField";
export const RentField = "RentField";
export const ShoppingField = "ShoppingField";
export const TravelField = "TravelField";
export const UncategorizedField = "UncategorizedField";
export const PaymentAccountField = "PaymentAccountField";

export const defaultPlanDateFieldValues = {
  [RepeatsField]: { ...defaultRepeatsFieldOption },
  [TargetDateField]: undefined,
  [StartsField]: { ...AlreadyStartedOption },
  [EndsField]: { ...CalendarYearOption },
  [CalendarYearStartField]: undefined,
  [YourAgeStartField]: undefined,
  [SpousesAgeStartField]: undefined,
  [CalendarYearEndField]: undefined,
  [YourAgeEndField]: undefined,
  [SpousesAgeEndField]: undefined,
};

export const CreateUpdateFlowScrollContainerDivID =
  "gbfpCreateUpdateFlowScrollContainer";
